import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import InputColor from 'react-input-color';
import { connect } from 'react-redux';
import { Select, Accordion, Button, Tooltip } from '@cimpress/react-components';
import { toSelectOption, getHexColor } from 'src/util/misc';
import { getAllBackgrounds } from 'src/selectors/layers';
import {
    ASSET_ACTIONS_OPTIONS,
    BLENDING_MODES_OPTIONS,
    PRODUCT_VARIABLE_TYPES,
    PRODUCT_VARIABLE_TYPES_OPTIONS,
    ASSET_ACTIONS,
    DEFAULT,
    BLENDING_MODES,
} from './constants';
import UploadFile from './UploadFile';
import { showAssetRemovalModal } from '../backgrounds/slice';
import AssetRemovalContainer from './assetRemoveModal';
import { downloadImagesToFileSystem } from 'src/util/upload';
import { getDefaultForAttributeValues } from './common';
import ErrorIcon from 'src/components/icons/ErrorIcon';
import FinishSelector from 'src/components/finish/finishSelector';

const DEFAULTOPTIONS = [{ value: 'Use Default', label: 'Use Default' }];
const actionOptions = DEFAULTOPTIONS.concat(ASSET_ACTIONS_OPTIONS);

const EachProductVariable = (props) => {
    const { configuration } = props;

    const isRequiredValuesChosen = (variableAttribute, variableOption, layer) => {
        if (!variableAttribute) {
            return false;
        }
        if (variableOption === PRODUCT_VARIABLE_TYPES.product && !layer) {
            return false;
        }
        return true;
    };
    const [nextStage, setNextStage] = useState(
        isRequiredValuesChosen(configuration.variableAttribute, configuration.variableOption, configuration.layer),
    );
    const options = useMemo(() => {
        if (!configuration.variableOption) {
            return [];
        }
        return toSelectOption(props.attributesOptions.map((option) => option.name));
    }, [configuration.variableOption, props.attributesOptions]);
    const onOptionsSelect = (key, newValue) => {
        props.updateConfiguration({ ...configuration, [key]: newValue });
    };
    const onVariableOptionChange = (chosenVariableOption) => {
        props.updateConfiguration({
            ...configuration,
            variableOption: chosenVariableOption.value,
            layer: null,
            document: null,
        });
    };
    const allLayers = useMemo(() => {
        if (!configuration.variableAttribute || !configuration.variableOption) {
            return [];
        }
        return props.assetOptions;
    }, [configuration.variableAttribute, configuration.variableOption, props.assetOptions]);
    const documentOptions = useMemo(() => {
        if (!configuration.variableAttribute || !configuration.variableOption) {
            return [];
        }
        return props.getDocumentOptions(configuration.variableAttribute);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configuration.variableAttribute, configuration.variableOption, props.getDocumentOptions]);

    const canGoToNextStep = useMemo(() => {
        const { variableAttribute, variableOption, layer } = configuration;
        return isRequiredValuesChosen(variableAttribute, variableOption, layer);
    }, [configuration]);
    const continueNextStep = () => {
        if (canGoToNextStep) {
            setNextStage(true);
            const attributeSelected = props.attributesOptions.find(
                (option) => option.name === configuration.variableAttribute,
            );
            const valueOptions = attributeSelected?.values ?? [];

            let valueDefault = undefined;
            switch (configuration.variableOption) {
                case PRODUCT_VARIABLE_TYPES.blending:
                    valueDefault = BLENDING_MODES.Normal;
                    break;
                case PRODUCT_VARIABLE_TYPES.finishes:
                    valueDefault = [];
                    break;
                case PRODUCT_VARIABLE_TYPES.product:
                    valueDefault = { action: DEFAULT };
            }

            const defaultValues = getDefaultForAttributeValues(valueOptions, valueDefault);
            props.updateConfiguration({ ...configuration, values: defaultValues });
        }
    };
    const deleteConfiguration = () => {
        props.removeConfiguration();
    };

    const onDownload = () => {
        const files = Object.keys(props.configuration.values)
            .map((key) => props.configuration.values[key])
            .filter((confgurationValue) => confgurationValue.value && confgurationValue.value.imageUrl)
            .map((confgurationValue) => ({
                url: confgurationValue.value.imageUrl,
                fileName: confgurationValue.value.name,
            }));

        downloadImagesToFileSystem(
            files,
            `${props.configuration.variableOption}-${props.configuration.variableAttribute}`,
        );
    };

    const getLayerTitle = () => {
        const { variableAttribute, variableOption, layer, document } = configuration;
        let text = 'Please configure attribute';
        if (nextStage && variableAttribute && variableOption) {
            text = `${variableOption} - ${variableAttribute}`;
            if (layer) {
                text += ` - ${layer.label}`;
            } else if (document) {
                text += ` - ${document}`;
            }
        }

        return (
            <div className='layers-header' data-testid={`product-variables-${text}`}>
                {configuration.errorMessage && (
                    <Tooltip direction='top' contents={configuration.errorMessage}>
                        <ErrorIcon />
                    </Tooltip>
                )}
                <div className='layers-header__text truncate_label' title={text}>
                    {text}
                </div>
                <button onClick={onDownload} style={{ marginRight: 8 }}>
                    <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/download-3-l.svg' />
                </button>
                <button onClick={deleteConfiguration}>
                    <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/bin-1-f.svg' />
                </button>
            </div>
        );
    };
    const onActionChange = (valueKey, chosenOption) => {
        props.updateConfiguration({
            ...configuration,
            values: {
                ...configuration.values,
                [valueKey]: {
                    action: chosenOption.value,
                },
            },
        });
    };
    const onBlendingModeChange = (valueKey, chosenOption) => {
        props.updateConfiguration({
            ...configuration,
            values: {
                ...configuration.values,
                [valueKey]: chosenOption.value,
            },
        });
    };

    const onFinishesChange = (valueKey, chosenOptions) => {
        props.updateConfiguration({
            ...configuration,
            values: {
                ...configuration.values,
                [valueKey]: chosenOptions.map((option) => option.id),
            },
        });
    };

    const onValueChange = (valueKey, value) => {
        let details = { ...configuration.values[valueKey] };
        if (!value) {
            details = { action: 'Use Default' };
        } else {
            details.value = value;
        }
        props.updateConfiguration({
            ...configuration,
            values: {
                ...configuration.values,
                [valueKey]: details,
            },
        });
    };

    const onRemoveBackground = (valueKey) => {
        const id = `${configuration.variableOption};${configuration.variableAttribute};${valueKey}`;
        props.showModal(id);
    };

    const layerDimensions = useMemo(() => {
        if (configuration.layer) {
            const theLayerDetails = props.backgroundLayers.find((each) => each.id === configuration.layer.value);
            if (theLayerDetails) {
                return {
                    width: theLayerDetails.width,
                    height: theLayerDetails.height,
                };
            }
            return {};
        }
        return {};
    }, [configuration.layer, props.backgroundLayers]);

    return (
        <Accordion className='product-variable-options-list' defaultOpen={true} title={getLayerTitle()}>
            <div className='accordion-body-wrapper'>
                {!nextStage && (
                    <>
                        <div data-testid='product-variables-type-selector'>
                            <Select
                                options={PRODUCT_VARIABLE_TYPES_OPTIONS}
                                value={toSelectOption(configuration.variableOption)}
                                onChange={onVariableOptionChange}
                                label='Select Variable Type'
                            />
                        </div>
                        <div data-testid='product-variables-attriubte-selector'>
                            <Select
                                options={options}
                                value={toSelectOption(configuration.variableAttribute)}
                                onChange={(chosenOption) => onOptionsSelect('variableAttribute', chosenOption.value)}
                                isClearable={false}
                                label='Select Attribute'
                                menuPortalTarget={document.body}
                            />
                        </div>
                        {configuration.variableOption === PRODUCT_VARIABLE_TYPES.product && (
                            <div data-testid='product-variables-layer-selector'>
                                <Select
                                    options={allLayers}
                                    value={configuration.layer}
                                    onChange={(chosenOption) => onOptionsSelect('layer', chosenOption)}
                                    isClearable={false}
                                    isSearchable={false}
                                    label='Select Layer'
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        )}
                        {(configuration.variableOption === PRODUCT_VARIABLE_TYPES.blending ||
                            configuration.variableOption === PRODUCT_VARIABLE_TYPES.finishes) && (
                            <div data-testid='product-variables-document-selector'>
                                <Select
                                    options={documentOptions}
                                    value={toSelectOption(configuration.document)}
                                    onChange={(chosenOption) => onOptionsSelect('document', chosenOption.value)}
                                    isClearable={false}
                                    isSearchable={false}
                                    label='Select Document'
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        )}
                        <Button data-testid='product-variables-ok-btn' onClick={continueNextStep}>
                            OK
                        </Button>
                    </>
                )}
                {nextStage &&
                    Object.keys(configuration.values || {}).map((eachValueKey) => (
                        <div key={eachValueKey} className='attribute-value-row'>
                            <div className='attribute-value-option'>
                                {eachValueKey}
                                {eachValueKey.startsWith('#') && (
                                    <div className='color' style={{ background: eachValueKey }} />
                                )}
                            </div>
                            {configuration.variableOption === PRODUCT_VARIABLE_TYPES.blending && (
                                <Select
                                    options={BLENDING_MODES_OPTIONS}
                                    label='Select'
                                    value={toSelectOption(configuration.values[eachValueKey])}
                                    onChange={(chosenOption) => onBlendingModeChange(eachValueKey, chosenOption)}
                                />
                            )}
                            {configuration.variableOption === PRODUCT_VARIABLE_TYPES.finishes && (
                                <FinishSelector
                                    id={eachValueKey}
                                    finishes={configuration.values[eachValueKey]}
                                    onSelect={onFinishesChange}
                                />
                            )}
                            {configuration.variableOption === PRODUCT_VARIABLE_TYPES.product && (
                                <>
                                    <Select
                                        options={actionOptions}
                                        label='Select'
                                        value={toSelectOption(configuration.values[eachValueKey].action)}
                                        onChange={(chosenOption) => onActionChange(eachValueKey, chosenOption)}
                                        menuPortalTarget={document.body}
                                    />
                                    {configuration.values[eachValueKey].action === ASSET_ACTIONS.REPLACE && (
                                        <UploadFile
                                            onChange={(fileDetails) => onValueChange(eachValueKey, fileDetails)}
                                            onBgRemove={() => onRemoveBackground(eachValueKey)}
                                            file={configuration.values[eachValueKey].value}
                                            {...layerDimensions}
                                        />
                                    )}
                                    {configuration.values[eachValueKey].action === ASSET_ACTIONS.FILL && (
                                        <InputColor
                                            initialValue={configuration.values[eachValueKey].value || '#808080'}
                                            onChange={(evt) => onValueChange(eachValueKey, getHexColor(evt.hex))}
                                            placement='top'
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    ))}
            </div>
            <AssetRemovalContainer />
        </Accordion>
    );
};

const optionShape = PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
});

EachProductVariable.propTypes = {
    configuration: PropTypes.shape({
        layer: PropTypes.shape(optionShape),
        variableAttribute: PropTypes.string,
        variableOption: PropTypes.string,
        document: PropTypes.string,
        values: PropTypes.arrayOf(PropTypes.any),
        errorMessage: PropTypes.string,
    }),
    updateConfiguration: PropTypes.func.isRequired,
    removeConfiguration: PropTypes.func.isRequired,
    assetOptions: PropTypes.arrayOf(optionShape),
    getDocumentOptions: PropTypes.func.isRequired,
    showModal: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    attributesOptions: PropTypes.arrayOf(PropTypes.object),
    backgroundLayers: PropTypes.array,
};

function mapStateToProps(state) {
    const backgroundLayers = getAllBackgrounds(state);

    return {
        backgroundLayers,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        showModal: (id) => dispatch(showAssetRemovalModal(id)),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EachProductVariable);
